
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface BandDisagreementDiscussionProps {
  selectedRecord: any;
  isSupervisor: boolean;
  resolutionOutcome: string;
  setResolutionOutcome: (value: string) => void;
  resolutionNotes: string;
  setResolutionNotes: (value: string) => void;
  onClose: () => void;
  onSaveAndClose: () => void;
}

const BandDisagreementDiscussion = ({
  selectedRecord,
  isSupervisor,
  resolutionOutcome,
  setResolutionOutcome,
  resolutionNotes,
  setResolutionNotes,
  onClose,
  onSaveAndClose
}: BandDisagreementDiscussionProps) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSaveAndCloseDialog, setShowSaveAndCloseDialog] = useState(false);

  // Set resolution outcome based on selectedRecord data for supervisors
  useEffect(() => {
    if (isSupervisor && selectedRecord?.resolution_outcome) {
      const outcomeValue = selectedRecord.resolution_outcome;
      
      if (outcomeValue === 'A') {
        setResolutionOutcome('Band A');
      } else if (outcomeValue === 'B') {
        setResolutionOutcome('Band B');
      } else if (outcomeValue === 'C') {
        setResolutionOutcome('Band C');
      } else if (outcomeValue === 'D') {
        setResolutionOutcome('Band D');
      } else {
        setResolutionOutcome('');
      }
    } else if (!isSupervisor && selectedRecord?.user_id) {
      // For team members, fetch from database
      const fetchResolutionOutcome = async () => {
        try {
          const { data, error } = await supabase
            .from('band_disagreements')
            .select('resolution_outcome')
            .eq('user_id', selectedRecord.user_id)
            .eq('status', 'Band disagreement resolution completed')
            .maybeSingle();

          if (error) {
            console.error('Error fetching resolution outcome:', error);
            return;
          }

          if (data?.resolution_outcome) {
            const outcomeValue = data.resolution_outcome;
            
            if (outcomeValue === 'A') {
              setResolutionOutcome('Band A');
            } else if (outcomeValue === 'B') {
              setResolutionOutcome('Band B');
            } else if (outcomeValue === 'C') {
              setResolutionOutcome('Band C');
            } else if (outcomeValue === 'D') {
              setResolutionOutcome('Band D');
            } else {
              setResolutionOutcome('');
            }
          } else {
            setResolutionOutcome('');
          }
        } catch (error) {
          console.error('Error fetching resolution outcome:', error);
          setResolutionOutcome('');
        }
      };

      fetchResolutionOutcome();
    } else {
      setResolutionOutcome('');
    }
  }, [selectedRecord?.user_id, selectedRecord?.resolution_outcome, isSupervisor, setResolutionOutcome]);

  if (!selectedRecord) return null;

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleSaveAndCloseClick = () => {
    setShowSaveAndCloseDialog(true);
  };

  const confirmCancel = () => {
    setShowCancelDialog(false);
    onClose();
  };

  const confirmSaveAndClose = async () => {
    setShowSaveAndCloseDialog(false);
    await onSaveAndClose();
    onClose();
  };

  const cancelCancelDialog = () => {
    setShowCancelDialog(false);
  };

  const cancelSaveAndCloseDialog = () => {
    setShowSaveAndCloseDialog(false);
  };

  const shouldShowSubmittedMessage = !isSupervisor && selectedRecord.status === "submitted";
  const shouldShowDisabledSupervisorActions = !isSupervisor && selectedRecord.status === "Band disagreement resolution completed";

  return (
    <>
      <Card className="bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-2 border-primary/40 shadow-2xl backdrop-blur-sm">
        <CardHeader className="pb-2 bg-gradient-to-r from-primary/20 to-purple-500/20 border-b border-primary/30 h-[80px]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-[16px] flex items-center gap-2">
              <div className="p-2 rounded-full bg-primary/20">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
              Band Disagreement Discussion
              <div className="text-[12px] text-gray-300 font-normal">
                {selectedRecord.employee_name || selectedRecord.user_id}
              </div>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelClick}
              className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-red-500/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          
          <div className="grid grid-cols-5 gap-2">
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 p-2 rounded-lg border border-blue-500/30">
              <Label className="text-blue-300 text-[9px] font-medium">Final Band</Label>
              <div className="text-white text-[10px] font-bold mt-1">{selectedRecord.final_band}</div>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 p-2 rounded-lg border border-green-500/30">
              <Label className="text-green-300 text-[9px] font-medium">Requested Band</Label>
              <div className="text-white text-[10px] font-bold mt-1">{selectedRecord.requested_band}</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 p-2 rounded-lg border border-yellow-500/30">
              <Label className="text-yellow-300 text-[9px] font-medium">Submitted</Label>
              <div className="text-white text-[10px] font-bold mt-1">{new Date(selectedRecord.submitted_date).toLocaleDateString()}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 p-2 rounded-lg border border-purple-500/30">
              <Label className="text-purple-300 text-[9px] font-medium">Status</Label>
              <div className="text-white text-[10px] font-bold mt-1">{selectedRecord.status.replace('_', ' ')}</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 p-2 rounded-lg border border-orange-500/30">
              <Label className="text-orange-300 text-[9px] font-medium">Triage</Label>
              <div className="text-white text-[10px] font-bold mt-1">
                {selectedRecord.triage_date ? new Date(selectedRecord.triage_date).toLocaleDateString() : '-'}
              </div>
            </div>
          </div>

          
          <div className="grid grid-cols-2 gap-6 h-[280px]">
            
            <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600/50 h-[280px]">
              <Label className="text-primary text-[13px] mb-2 block">Disagreement Details</Label>
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-300 text-[10px]">Disagreement Reason(s)</Label>
                  <Textarea
                    value={selectedRecord.disagreement_reason}
                    readOnly
                    className="bg-slate-700/50 border-slate-600/50 text-white text-[11px] h-[20px] resize-none"
                  />
                </div>
                
                {selectedRecord.supporting_evidence && (
                  <div>
                    <Label className="text-gray-300 text-[10px]">Supporting Evidence</Label>
                    <Textarea
                      value={selectedRecord.supporting_evidence}
                      readOnly
                      className="bg-slate-700/50 border-slate-600/50 text-white text-[11px] h-[20px] resize-none"
                    />
                  </div>
                )}
                
              </div>
            </div>

            
            {(isSupervisor || shouldShowDisabledSupervisorActions) && (
              <div className="bg-gradient-to-br from-primary/10 to-purple-500/10 p-4 rounded-lg border border-primary/30 h-[280px]">
                <Label className="text-primary text-[13px] mb-3 block">Supervisor Actions</Label>
                <div className="space-y-3">
                  <div className="w-full max-w-[180px]">
                    <Label className="text-gray-300 text-[10px]">Resolution Outcome</Label>
                    <Select 
                      value={resolutionOutcome} 
                      onValueChange={setResolutionOutcome}
                      disabled={shouldShowDisabledSupervisorActions}
                    >
                      <SelectTrigger className="bg-slate-700/50 border-slate-600/50 text-white text-[11px] h-8">
                        <SelectValue placeholder="Select outcome" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Band A">Band A</SelectItem>
                        <SelectItem value="Band B">Band B</SelectItem>
                        <SelectItem value="Band C">Band C</SelectItem>
                        <SelectItem value="Band D">Band D</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-gray-300 text-[10px]">Resolution Notes</Label>
                    <Textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      placeholder="Enter resolution notes..."
                      className="bg-slate-700/50 border-slate-600/50 text-white text-[11px] h-[110px] resize-none"
                      disabled={shouldShowDisabledSupervisorActions}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={handleCancelClick}
              className="bg-white text-black text-[13px]"
            >
              Cancel
            </Button>
            {isSupervisor && (
              <Button
                onClick={handleSaveAndCloseClick}
                className="bg-primary text-black text-[13px]"
              >
                Save & Close
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Show Band Disagreement Request(s)?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to close this discussion?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={cancelCancelDialog}
              className="bg-gray-600 text-white border-gray-500 hover:bg-gray-700"
            >
              Stay in Discussion
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmCancel}
              className="bg-primary text-black hover:bg-primary/90"
            >
              Yes, Show Request(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save & Close Confirmation Dialog */}
      <AlertDialog open={showSaveAndCloseDialog} onOpenChange={setShowSaveAndCloseDialog}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Save & Show Band Disagreement Requests?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to save this resolution and display all team members' band disagreement requests? This will show confidential information about other team members.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={cancelSaveAndCloseDialog}
              className="bg-gray-600 text-white border-gray-500 hover:bg-gray-700"
            >
              Stay in Discussion
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmSaveAndClose}
              className="bg-primary text-black hover:bg-primary/90"
            >
              Yes, Save & Show All Requests
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BandDisagreementDiscussion;
